import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import { PostCheckoutCommand } from '../../../services/command';
import SCUIDropzone from '../../../atomic/molecules/SCUIDropzone/SCUIDropzone';
import Swal from 'sweetalert2';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import DescriptionIcon from '@mui/icons-material/Description';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import TimeHelper from '../../../utils/time';
import PersonIcon from '@mui/icons-material/Person';
import { DCAU } from '../digitalCard.classes';

interface IProps {
    selectedRow?: any;
    onClose?: any;
    refresh?: any;
}

const InfoRow: FC<{ icon: React.ReactNode; label: string; value?: string }> = ({ icon, label, value }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: 160, flexShrink: 0, color: 'text.secondary' }}>
            {icon}
            <Typography variant="caption" fontWeight={600} color="text.secondary">
                {label}
            </Typography>
        </Box>
        <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
            {value || '—'}
        </Typography>
    </Box>
);

export const AddUpdatedigitalCardDrawer: FC<IProps> = ({ selectedRow, onClose, refresh }) => {
    const [file, setFile] = useState<any>('');
    const [digitalCardName, setDigitalCardName] = useState<string>(selectedRow.title);
    const [activity, setActivity] = useState<number>(selectedRow.templateStatus);
    const [description, setDescription] = useState<any>(selectedRow.description);

    const { t } = useTranslation();

    const createDate = selectedRow.createdDate;
    const modifiedBy = selectedRow.modifiedBy;
    const modifiedDate = selectedRow.modifiedDate;
    const createdBy = selectedRow.createdBy;

    const handleUpload = (files: any) => {
        const file = files?.[0];
        if (file) setFile(file);
    };

    const validateForm = () => {
        return digitalCardName && description && (file?.name || selectedRow.imageUrl);
    };

    const createFormData = () => {
        const formData = new FormData();
        formData.append('digitalCardTemplateId', selectedRow.id);
        formData.append('displayOrder', selectedRow.displayOrder);
        formData.append('description', description);
        formData.append('title', digitalCardName);
        formData.append('imageUrl', file?.name ? '' : selectedRow.imageUrl);
        formData.append('iconUrl', file?.name ? '' : selectedRow.imageUrl);
        formData.append('templateStatus', activity ? '1' : '0');
        formData.append('File', file || '');
        return formData;
    };

    const handleSuccess = (successMessage: string) => {
        Swal.fire(t('general.success'), successMessage, 'success');
        onClose();
        refresh();
    };

    const handleError = (error: any) => {
        const errorMessage = error?.data?.Key ? `${error?.data?.Key}` : t('general.anErrorOccured');
        Swal.fire(t('general.error'), errorMessage, 'error');
        onClose();
    };

    const handleAddDigitalCardTemplate = () => {
        if (!validateForm()) {
            Swal.fire(t('general.error'), t('general.emptyFieldControlText'), 'error');
            return;
        }
        PostCheckoutCommand.AddDigitalCardTemplates(createFormData())
            .then((res) => { if (res) handleSuccess(t('digitalGiftCard.digitalGiftCardAdded')); })
            .catch(handleError);
    };

    const handleUpdateDigitalCardTemplate = () => {
        if (!validateForm()) {
            Swal.fire(t('general.error'), t('general.emptyFieldControlText'), 'error');
            return;
        }
        PostCheckoutCommand.UpdateDigitalCardTemplates(createFormData())
            .then((res) => { if (res) handleSuccess(t('digitalGiftCard.digitalGiftCardUpdated')); })
            .catch(handleError);
    };

    const isEditing = selectedRow?.id > 0;

    return (
        <Box className={DCAU.drawerRoot} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* ── Sticky header ── */}
            <SCUIGeneralDrawerHeader
                header={isEditing ? t('digitalGiftCard.digitalGiftCardUpdate') : t('digitalGiftCard.digitalGiftCardCreate')}
                onClose={onClose}
            />

            {/* ── Scrollable body ── */}
            <Box className={DCAU.drawerBody} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                <Stack spacing={3}>

                    {/* Photo */}
                    <Box className={DCAU.sectionPicture}>
                        <Box className={DCAU.pictureToolbar} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                            <InsertPhotoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('digitalGiftCard.picture')}
                            </Typography>
                        </Box>
                        <Box className={DCAU.picturePreviewRow} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {/* Preview: existing image or new-file chip */}
                            {isEditing ? (
                                file?.name ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
                                        <InsertPhotoIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                        <Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>{file.name}</Typography>
                                    </Box>
                                ) : (
                                    selectedRow.imageUrl && (
                                        <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                                            <img
                                                src={selectedRow.imageUrl}
                                                style={{ width: 180, height: 90, objectFit: 'cover', display: 'block' }}
                                                alt="card"
                                            />
                                        </Box>
                                    )
                                )
                            ) : (
                                file?.name && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover' }}>
                                        <InsertPhotoIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                        <Typography variant="caption">{file.name}</Typography>
                                    </Box>
                                )
                            )}
                            {/* Dropzone – always visible unless a new file is chosen */}
                            {!file?.name && (
                                <Box className={DCAU.dropzoneWrap} sx={{ flex: 1, minWidth: 160, maxWidth: 220 }}>
                                    <SCUIDropzone onChange={handleUpload} />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Divider />

                    {/* Card name */}
                    <TextField
                        className={DCAU.inputTextfieldName}
                        fullWidth
                        label={t('digitalGiftCard.digitalGiftCardName')}
                        value={digitalCardName}
                        slotProps={{ htmlInput: { startAdornment: <CardGiftcardIcon /> } }}
                        onChange={(e) => setDigitalCardName(e.target.value)}
                    />

                    {/* Description */}
                    <TextField
                        className={DCAU.inputTextfieldDescription}
                        fullWidth
                        label={t('digitalGiftCard.digitalGiftCardDescription')}
                        value={description}
                        slotProps={{ htmlInput: { startAdornment: <DescriptionIcon /> } }}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    {/* Status toggle */}
                    <Box
                        className={DCAU.rowTemplateStatus}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            bgcolor: 'action.hover',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ToggleOnIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={600}>
                                {t('digitalGiftCard.status')}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            label=""
                            control={
                                <Switch
                                    className={DCAU.inputSwitchTemplateStatus}
                                    checked={activity === 1}
                                    onChange={(e) => setActivity(e.target.checked ? 1 : 0)}
                                />
                            }
                        />
                    </Box>

                    {/* Metadata – only for edit mode */}
                    {isEditing && (
                        <>
                            <Divider />
                            <Stack className={DCAU.metadataSection} spacing={0}>
                                <InfoRow
                                    icon={<PersonIcon sx={{ fontSize: 15 }} />}
                                    label={t('digitalGiftCard.createdBy')}
                                    value={createdBy}
                                />
                                <InfoRow
                                    icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
                                    label={t('digitalGiftCard.createDate')}
                                    value={createDate ? TimeHelper.formatDateHour(createDate) : undefined}
                                />
                                <InfoRow
                                    icon={<PersonIcon sx={{ fontSize: 15 }} />}
                                    label={t('digitalGiftCard.updatedBy')}
                                    value={modifiedBy}
                                />
                                <InfoRow
                                    icon={<UpdateIcon sx={{ fontSize: 15 }} />}
                                    label={t('digitalGiftCard.updateDate')}
                                    value={modifiedDate ? TimeHelper.formatDateHour(modifiedDate) : undefined}
                                />
                            </Stack>
                        </>
                    )}

                </Stack>
            </Box>

            {/* ── Sticky footer ── */}
            <Box
                className={DCAU.footer}
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <SCUIButton
                    className={DCAU.inputButtonSave}
                    onClick={() => isEditing ? handleUpdateDigitalCardTemplate() : handleAddDigitalCardTemplate()}
                    type={'primary'}
                    sizeType={'fixed'}
                    size={'l'}
                    text={t('general.save')}
                />
            </Box>
        </Box>
    );
};
